import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useToast } from '@shared/hooks/use-toast';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PreInvoicesApiAdapter } from '../adapters/PreInvoicesApiAdapter';

/**
 * useInvoicePreInvoice - Hook para facturar una prefactura
 * 
 * Ejecuta POST /invoices/pre-invoices/{id}/invoice para marcar una prefactura como facturada (CLOSED).
 * Maneja estados de loading, éxito y error con toasts apropiados.
 * 
 * @returns {Object} Función invoicePreInvoice y estado isInvoicing
 */
export const useInvoicePreInvoice = () => {
  const { t } = useTranslation('preInvoicesModule');
  const { toast } = useToast();
  const [isInvoicing, setIsInvoicing] = useState(false);

  const invoicePreInvoice = async (
    preInvoiceId: number,
    onSuccess?: () => void
  ) => {
    setIsInvoicing(true);

    try {
      console.log('🔍 useInvoicePreInvoice - Facturando prefactura:', { preInvoiceId });

      const httpClient = createHttpClient(apiClientWH);
      const adapter = new PreInvoicesApiAdapter(httpClient);
      const response = await adapter.invoicePreInvoice(preInvoiceId);

      console.log('🔍 useInvoicePreInvoice - Respuesta:', response);

      if (response.success) {
        // 🎯 Mostrar toast de éxito con mensaje de la API
        toast({
          title: t('toast.invoice_success.title', 'Prefactura facturada'),
          description: response.message || t('toast.invoice_success.description', 'La prefactura se ha facturado exitosamente'),
          variant: 'default',
        });

        // 🎯 Ejecutar callback de éxito (refetch, etc.)
        onSuccess?.();
      } else {
        // 🎯 Mostrar error devuelto por la API
        toast({
          title: t('toast.invoice_error.title', 'Error al facturar'),
          description: response.message,
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.log('🔍 useInvoicePreInvoice - Error:', error);

      // 🎯 Mostrar error inesperado
      toast({
        title: t('toast.invoice_error.title', 'Error al facturar'),
        description: error?.message || t('toast.invoice_error.unexpected', 'Error inesperado al facturar la prefactura. Inténtalo de nuevo.'),
        variant: 'destructive',
      });
    } finally {
      setIsInvoicing(false);
    }
  };

  return {
    invoicePreInvoice,
    isInvoicing,
  };
};

